import { forwardRef, useImperativeHandle } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";
import {
    fetchImageAsBase64,
    getExcelExportNumberFormat,
    getExcelExportTemporalType,
    resolveExcelExportTemporalValue,
} from "../../utils/helpers";
import {
    isDecimalExportColumn,
    resolveExcelExportNumericValue,
    EXCEL_DECIMAL_FORMAT,
} from "unmsght-report-package";

const ExportGraphToCsv = forwardRef(
    (
        {
            data = [],
            defaultColumns = [],
            selectedReport = {},
            reportingPeriodValue,
            organizationName,
            chartScreenshot,
            reportLastFetched,
            relatedOrganizationNames,
            user,
            initialPolicyDivision = [],
            claimCoverageTypes = [],
            disclaimer = "",
            filterStatusValues = [],
            columnFilter,
        },
        ref
    ) => {
        const exportToExcel = async () => {
            if (!data || data.length === 0) {
                console.warn("No data to export.");
                return;
            }

            const workbook = new ExcelJS.Workbook();

            //COVER SHEET (Graph)
            const coverSheet = workbook.addWorksheet("Graph");

            try {
                const imageBase64 = await fetchImageAsBase64(chartScreenshot);
                if (imageBase64) {
                    const imageId = workbook.addImage({
                        base64: imageBase64,
                        extension: "jpeg",
                    });
                    coverSheet.addImage(imageId, {
                        tl: { col: 0, row: 0 },
                        br: { col: 10, row: 20 },
                    });
                }
            } catch (err) {
                console.warn("Image load failed:", err);
            }

            //REPORT DATA SHEET
            const reportSheet = workbook.addWorksheet("Data");

            const headers = defaultColumns.map(
                (col) => col.header || col.accessorKey || "Column"
            );
            const keys = defaultColumns.map((col) => col.accessorKey);
            const temporalExportTypes = defaultColumns.map((column) =>
                getExcelExportTemporalType(column)
            );

            reportSheet.addRow(headers);

            // Batch row addition
            const formattedRows = data.map((row) =>
                keys.map((key) =>
                    row[key] === null ||
                    row[key] === undefined ||
                    row[key] === ""
                        ? "—"
                        : row[key]
                )
            );
            reportSheet.addRows(formattedRows);

            // Convert date-like columns to real Excel dates/datetimes for sorting/filtering
            temporalExportTypes.forEach((temporalExportType, columnIndex) => {
                if (!temporalExportType) {
                    return;
                }

                const key = keys[columnIndex];
                for (let rowIndex = 0; rowIndex < data.length; rowIndex += 1) {
                    const temporalExportValue = resolveExcelExportTemporalValue(
                        data[rowIndex]?.[key],
                        temporalExportType
                    );
                    if (!temporalExportValue) {
                        continue;
                    }

                    const cell = reportSheet.getCell(rowIndex + 2, columnIndex + 1);
                    cell.value = temporalExportValue.parsedValue;
                    cell.numFmt = getExcelExportNumberFormat(
                        temporalExportValue.resolvedType
                    );
                }
            });

            // Additive decimal typing: only columns the backend tags
            // `sortingMethod: "decimal"` become real, summable Excel numbers.
            defaultColumns.forEach((column, columnIndex) => {
                if (!isDecimalExportColumn(column)) {
                    return;
                }

                const key = keys[columnIndex];
                for (let rowIndex = 0; rowIndex < data.length; rowIndex += 1) {
                    const numericExportValue = resolveExcelExportNumericValue(
                        data[rowIndex]?.[key]
                    );
                    if (!numericExportValue) {
                        continue;
                    }

                    const cell = reportSheet.getCell(rowIndex + 2, columnIndex + 1);
                    cell.value = numericExportValue.parsedValue;
                    cell.numFmt = EXCEL_DECIMAL_FORMAT;
                }
            });

            // Efficient auto-width columns
            reportSheet.columns.forEach((column, idx) => {
                let maxLength = headers[idx]?.length ?? 10;
                column.eachCell({ includeEmpty: false }, (cell) => {
                    const len = String(cell.value || "").length;
                    if (len > maxLength) maxLength = len;
                });
                column.width = Math.min(maxLength + 2, 50); // Cap at 50 for readability
            });

            //FILTERS SHEET
            const filtersSheet = workbook.addWorksheet("Filter Criteria");

            // Helper to add a blank line for spacing
            const addSpacer = () => filtersSheet.addRow([]);

            //Report Details
            filtersSheet.addRow(["Filter Criteria / Report Details"]);
            addSpacer();

            filtersSheet.addRows([
                [
                    "Report",
                    selectedReport?.Name || selectedReport?.title || "—",
                ],
                ["Date export run", reportLastFetched || "—"],
                ["Organization Name", organizationName || "—"],
                [
                    "Related Organizations",
                    Array.isArray(relatedOrganizationNames)
                        ? relatedOrganizationNames.join(", ")
                        : relatedOrganizationNames || "None",
                ],
                [
                    "Run by user",
                    user?.isUserInternal
                        ? "Unum Internal"
                        : user?.userEmail || "—",
                ],
                ["Disclaimer", disclaimer || "—"],
            ]);

            addSpacer();

            //Additional Filters / Column Filters
            filtersSheet.addRow(["Additional filters" , "Column filters"]);
            addSpacer();
            // Add column filters if present
            if (Array.isArray(columnFilter) && columnFilter.length > 0) {

                columnFilter.forEach((filter) => {
                    filtersSheet.addRow([
                        filter.columnName || filter.accessorKey || "—",
                        filter.filterValue || "—",
                    ]);
                });
            }
            addSpacer();
            filtersSheet.addRows([
                ["Filter", "Filter Values"],
                [
                    "Effective start date",
                    reportingPeriodValue?.minimumStartDate || "—",
                ],
                [
                    "Effective end date",
                    reportingPeriodValue?.maximumEndDate || "—",
                ],
                [
                    "Coverage type",
                    claimCoverageTypes.length > 0
                        ? claimCoverageTypes.join(", ")
                        : "—",
                ],
                [
                    "Status",
                    filterStatusValues.length > 0
                        ? filterStatusValues.join(", ")
                        : "—",
                ],
                [
                    "Policies and divisions",
                    initialPolicyDivision.length > 0
                        ? initialPolicyDivision.join(", ")
                        : "—",
                ],
            ]);

            // Adjust column width for readability
            filtersSheet.getColumn(1).width = 35;
            filtersSheet.getColumn(2).width = 60;

            //Save File
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const fileName = `${selectedReport?.Name || "Report"}.xlsx`;
            saveAs(blob, fileName);
        };

        useImperativeHandle(ref, () => ({
            exportToExcel,
        }));

        return null;
    }
);

ExportGraphToCsv.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    defaultColumns: PropTypes.arrayOf(
        PropTypes.shape({
            header: PropTypes.string,
            accessorKey: PropTypes.string,
        })
    ),
    selectedReport: PropTypes.shape({
        Name: PropTypes.string,
        title: PropTypes.string,
    }),
    reportingPeriodValue: PropTypes.shape({
        minimumStartDate: PropTypes.string,
        maximumEndDate: PropTypes.string,
    }),
    organizationName: PropTypes.string,
    chartScreenshot: PropTypes.string,
    reportLastFetched: PropTypes.string,
    relatedOrganizationNames: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
    ]),
    user: PropTypes.shape({
        isUserInternal: PropTypes.bool,
        userEmail: PropTypes.string,
    }),
    initialPolicyDivision: PropTypes.arrayOf(PropTypes.string),
    claimCoverageTypes: PropTypes.arrayOf(PropTypes.string),
    disclaimer: PropTypes.string,
    filterStatusValues: PropTypes.arrayOf(PropTypes.string),
};

export default ExportGraphToCsv;
